import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  Link as MuiLink,
  MenuItem,
  Slider,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import RefreshIcon from '@mui/icons-material/Refresh';
import SavingsIcon from '@mui/icons-material/Savings';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddCardIcon from '@mui/icons-material/AddCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

import { useEffectiveUser } from '@src/components/layout/AppLayout/ClientViewSwitcher';
import { toast } from '@src/components/core/toaster';
import {
  chargeNow,
  createSetupIntent,
  detachPaymentMethod,
  enablePrepaid,
  fetchPrepaidTerms,
  fetchBillingLiveStatus,
  fetchBillingProfileForClient,
  fetchPaymentMethods,
  listThresholdCharges,
  registerPendingBankVerification,
  resumeBilling,
  setDefaultPaymentMethod,
  updateBillingProfile,
} from '@src/api/billingThreshold';
import { retrieveInvoiceClientSettings } from '@src/api/invoices';
import AddBankDialog from '@src/components/billing/AddBankDialog';
import EnablePrepaidConfirmDialog from '@src/components/billing/EnablePrepaidConfirmDialog';
import RemovePaymentMethodDialog from '@src/components/billing/RemovePaymentMethodDialog';
import {
  cardExpirationStatus,
  formatCardExpiration,
  formatLastChargeLine,
  isUnverifiedBank as isUnverifiedBankFn,
  summarizePaymentHealth,
} from '@src/components/billing/paymentMethodStatus';

// ── Add Card dialog (Stripe Elements) ─────────────────────────────────

function AddCardForm({ clientSecret, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError(null);
    const result = await stripe.confirmCardSetup(clientSecret, {
      payment_method: { card: elements.getElement(CardElement) },
    });
    if (result.error) {
      setError(result.error.message);
      setSaving(false);
      return;
    }
    if (result.setupIntent?.status === 'succeeded') {
      toast.success('Card attached successfully.');
      // Hand the new PM id back to the parent so it can auto-set as
      // default when this was the first card on file.
      onSuccess(result.setupIntent.payment_method);
    } else {
      setError('Setup did not complete. Please try again.');
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Enter the client's card details. Stripe tokenizes the card in the
          browser — the number never reaches our servers.
        </DialogContentText>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
          }}
        >
          <CardElement
            options={{
              style: {
                base: { fontSize: '16px', color: '#333' },
                invalid: { color: '#dc2626' },
              },
            }}
          />
        </Box>
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={!stripe || saving}>
          {saving ? 'Saving…' : 'Save card'}
        </Button>
      </DialogActions>
    </form>
  );
}

function AddCardDialog({ open, onClose, profileId, isFirstCard, onAttached }) {
  const [intent, setIntent] = React.useState(null);
  const [stripePromise, setStripePromise] = React.useState(null);
  const [loadError, setLoadError] = React.useState(null);

  React.useEffect(() => {
    if (!open || !profileId) return;
    let cancelled = false;
    setIntent(null);
    setLoadError(null);
    createSetupIntent(profileId, 'card')
      .then((resp) => {
        if (cancelled) return;
        const data = resp?.data;
        if (!data?.client_secret || !data?.publishable_key) {
          setLoadError('Stripe is not configured. Check publishable key + secret in settings.');
          return;
        }
        setIntent(data);
        setStripePromise((prev) => prev || loadStripe(data.publishable_key));
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.response?.data?.detail || err?.message || 'Failed to start setup.');
      });
    return () => {
      cancelled = true;
    };
  }, [open, profileId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add card</DialogTitle>
      {loadError ? (
        <>
          <DialogContent>
            <Alert severity="error">{loadError}</Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </>
      ) : !intent || !stripePromise ? (
        <DialogContent>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Starting Stripe setup…
            </Typography>
          </Stack>
        </DialogContent>
      ) : (
        <Elements stripe={stripePromise}>
          <AddCardForm
            clientSecret={intent.client_secret}
            onSuccess={async (newPmId) => {
              // First card on file → auto-mark as primary so auto-charges
              // have a clear target. If there were already methods, leave
              // the existing default in place; staff can manually flip.
              if (isFirstCard && newPmId) {
                try {
                  await setDefaultPaymentMethod(profileId, newPmId);
                } catch {
                  /* non-fatal — the most-recent fallback still works */
                }
              }
              onAttached();
              onClose();
            }}
            onCancel={onClose}
          />
        </Elements>
      )}
    </Dialog>
  );
}

// ACH-only is intentionally absent — a card is always required as a
// backup. ACH can fail silently for 3-5 days, so we never let it be the
// only payment method on file.
const ACH_PRIORITIES = new Set(['card_then_ach', 'ach_then_card']);

const PRIORITY_OPTIONS = [
  { value: 'card', label: 'Card only' },
  { value: 'card_then_ach', label: 'Card, fall back to ACH' },
  { value: 'ach_then_card', label: 'ACH, fall back to Card (recommended)' },
];

const BILLING_MODE_OPTIONS = [
  { value: 'threshold', label: 'Threshold — real-time auto-charge' },
  {
    value: 'client_credit_line',
    label: 'Client-funded credit line — fee-only invoice',
  },
  { value: 'prepay', label: 'Prepay balance' },
];

const NET_TERMS_OPTIONS = [
  { value: 15, label: 'Net 15' },
  { value: 30, label: 'Net 30' },
  { value: 45, label: 'Net 45' },
  { value: 60, label: 'Net 60' },
];

const STATE_CHIP_COLORS = {
  healthy: 'success',
  warning: 'warning',
  paused: 'error',
  disabled: 'default',
};

const OUTCOME_CHIP_COLORS = {
  pending: 'default',
  charged: 'success',
  retried_success: 'success',
  failed: 'error',
  paused: 'error',
};

const MAX_UNBILLED_MULTIPLIER = 1.5;

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

const formatCurrency2dp = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const niceStep = (max) => {
  if (max <= 1500) return 50;
  if (max <= 5000) return 100;
  if (max <= 10000) return 250;
  return 500;
};

const FORM_DEFAULTS = {
  billing_mode: 'threshold',
  net_terms_days: 30,
  client_billing_ui_enabled: false,
  threshold_billing_enabled: false,
  threshold_amount: 1000,
  payment_method_priority: 'card',
  auto_pause_on_failure: true,
  attach_meta_charges: true,
  threshold_locked_by_staff: false,
  notes: '',
};

export default function ClientBillingThresholdPage() {
  const { clientId } = useParams();
  const user = useEffectiveUser() || {};
  const isStaff = user.is_staff || user.is_superuser;

  const [loading, setLoading] = useState(true);
  const [methodsLoading, setMethodsLoading] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [error, setError] = useState(null);
  const [client, setClient] = useState(null);
  const [profile, setProfile] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [numericClientId, setNumericClientId] = useState(null);
  const [form, setForm] = useState(FORM_DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const [confirmCharge, setConfirmCharge] = useState(false);
  const [confirmResume, setConfirmResume] = useState(false);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [addBankOpen, setAddBankOpen] = useState(false);
  const [methodToRemove, setMethodToRemove] = useState(null);
  const [prepaidConfirmOpen, setPrepaidConfirmOpen] = useState(false);

  const refreshPaymentMethods = useCallback(async () => {
    if (!profile?.id) return;
    setMethodsLoading(true);
    try {
      const resp = await fetchPaymentMethods(profile.id);
      setPaymentMethods(resp?.data || null);
    } catch {
      setPaymentMethods(null);
    } finally {
      setMethodsLoading(false);
    }
  }, [profile?.id]);

  const tierAchEligible = Boolean(liveStatus?.tier_ach_eligible);
  const tierName = liveStatus?.tier_name || null;
  const cardOnly = form.payment_method_priority === 'card';
  const savings = liveStatus?.fee_projection?.monthly_savings_on_ach;
  const bounds = liveStatus?.threshold_bounds;

  const sliderMin = bounds ? Math.floor(Number(bounds.min)) : 300;
  const sliderMax = bounds ? Math.ceil(Number(bounds.max)) : 10000;
  const sliderStep = niceStep(sliderMax);
  const recommendedThreshold = liveStatus
    ? Math.round(Number(liveStatus.recommended_threshold || 0))
    : null;

  const syncFormFromProfile = (next) => {
    if (!next) {
      setForm(FORM_DEFAULTS);
      return;
    }
    setForm({
      billing_mode: next.billing_mode || 'threshold',
      net_terms_days: Number(next.net_terms_days ?? 30),
      client_billing_ui_enabled: Boolean(next.client_billing_ui_enabled),
      threshold_billing_enabled: Boolean(next.threshold_billing_enabled),
      threshold_amount: Number(next.threshold_amount ?? 1000),
      payment_method_priority: next.payment_method_priority || 'card',
      auto_pause_on_failure: next.auto_pause_on_failure !== false,
      attach_meta_charges: next.attach_meta_charges !== false,
      threshold_locked_by_staff: Boolean(next.threshold_locked_by_staff),
      notes: next.notes || '',
    });
  };

  const loadAll = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const [clientResp, profileResp] = await Promise.all([
        retrieveInvoiceClientSettings(clientId).catch(() => null),
        fetchBillingProfileForClient(clientId),
      ]);
      setClient(clientResp?.data || null);
      const resolvedClientId =
        profileResp?.data?.client_id ?? clientResp?.data?.id ?? null;
      setNumericClientId(resolvedClientId);
      const profileData = profileResp?.data?.profile || null;
      setProfile(profileData);
      syncFormFromProfile(profileData);

      if (profileData) {
        try {
          const statusResp = await fetchBillingLiveStatus(profileData.id);
          setLiveStatus(statusResp?.data || null);
        } catch {
          setLiveStatus(null);
        }
      } else {
        setLiveStatus(null);
      }
      setLoading(false);

      if (profileData) {
        setMethodsLoading(true);
        setLedgerLoading(true);
        fetchPaymentMethods(profileData.id)
          .then((resp) => setPaymentMethods(resp?.data || null))
          .catch(() => setPaymentMethods(null))
          .finally(() => setMethodsLoading(false));
        listThresholdCharges({ client_id: resolvedClientId })
          .then((resp) => {
            const ledger = resp?.data;
            setLedgerEntries(
              Array.isArray(ledger) ? ledger : ledger?.results || [],
            );
          })
          .catch(() => setLedgerEntries([]))
          .finally(() => setLedgerLoading(false));
      } else {
        setPaymentMethods(null);
        setLedgerEntries([]);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load.');
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!profile?.id) return undefined;
    const interval = setInterval(async () => {
      try {
        const resp = await fetchBillingLiveStatus(profile.id);
        setLiveStatus(resp?.data || null);
      } catch {
        /* silent */
      }
    }, 30_000);
    return () => clearInterval(interval);
  }, [profile?.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const threshold = Number(form.threshold_amount) || 0;
      const payload = {
        billing_mode: form.billing_mode,
        net_terms_days: Number(form.net_terms_days) || 30,
        client_billing_ui_enabled: form.client_billing_ui_enabled,
        threshold_billing_enabled: form.threshold_billing_enabled,
        threshold_amount: threshold.toFixed(2),
        max_unbilled_amount: (threshold * MAX_UNBILLED_MULTIPLIER).toFixed(2),
        payment_method_priority: form.payment_method_priority,
        auto_pause_on_failure: form.auto_pause_on_failure,
        attach_meta_charges: form.attach_meta_charges,
        threshold_locked_by_staff: form.threshold_locked_by_staff,
        notes: form.notes,
      };
      const resp = await updateBillingProfile(profile.id, payload);
      setProfile(resp?.data || null);
      syncFormFromProfile(resp?.data);
      toast.success('Billing settings saved.');
      await loadAll();
    } catch (err) {
      // ACH-primary-requires-prepaid gate was removed, so the serializer
      // no longer returns that error. Just surface whatever went wrong.
      const data = err?.response?.data || {};
      const detail =
        data?.detail || JSON.stringify(data) || err?.message;
      toast.error(`Save failed: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  const handleApplyRecommendation = () => {
    if (recommendedThreshold && recommendedThreshold > 0) {
      setForm((prev) => ({ ...prev, threshold_amount: recommendedThreshold }));
    }
  };

  const handleChargeNow = async () => {
    setConfirmCharge(false);
    setBusyAction('charge');
    try {
      const resp = await chargeNow(profile.id);
      const data = resp?.data || {};
      const outcome = data.outcome;
      if (outcome === 'charged' || outcome === 'processing') {
        toast.success(`Charged ${formatCurrency2dp(data.spend)}.`);
      } else if (outcome === 'below_threshold') {
        toast.warning(data.error || 'Below minimum chargeable amount.');
      } else {
        toast.error(`Charge failed: ${data.error || outcome}`);
      }
      await loadAll();
    } catch (err) {
      toast.error(`Charge failed: ${err?.response?.data?.detail || err?.message}`);
    } finally {
      setBusyAction(null);
    }
  };

  // Opens the confirm dialog — the dialog itself fetches the active terms,
  // shows the body + amount, and submits the acceptance + enroll call.
  const handleEnablePrepaid = () => {
    setPrepaidConfirmOpen(true);
  };

  const handleResume = async () => {
    setConfirmResume(false);
    setBusyAction('resume');
    try {
      const resp = await resumeBilling(profile.id);
      const data = resp?.data || {};
      if (data.outcome === 'resumed') {
        toast.success(`Resumed from ${data.previous_state}.`);
      } else {
        toast.info('Already healthy.');
      }
      await loadAll();
    } catch (err) {
      toast.error(`Resume failed: ${err?.response?.data?.detail || err?.message}`);
    } finally {
      setBusyAction(null);
    }
  };

  const progressPct = useMemo(() => {
    const v = liveStatus?.threshold_progress_pct;
    if (typeof v !== 'number') return 0;
    return Math.max(0, Math.min(100, v));
  }, [liveStatus]);

  if (!isStaff) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">Staff access only.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5">Threshold Billing</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {client?.name || `Client ${clientId}`}
            </Typography>
            {tierName && (
              <Chip size="small" label={tierName} variant="outlined" />
            )}
          </Stack>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={loadAll}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* CARD 1 — Status + Settings combined */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {/* Top strip: state + unbilled progress */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Chip
              label={liveStatus?.state_display || 'Healthy'}
              color={STATE_CHIP_COLORS[liveStatus?.state || 'healthy']}
              sx={{ minWidth: 100 }}
            />
            <Box sx={{ flex: 1, width: '100%' }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Unbilled spend
                </Typography>
                <Typography variant="caption">
                  {formatCurrency2dp(liveStatus?.unbilled_spend)} /{' '}
                  {formatCurrency2dp(liveStatus?.threshold_amount)}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progressPct}
                color={
                  progressPct >= 100 ? 'error' : progressPct >= 75 ? 'warning' : 'primary'
                }
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>
          </Stack>

          <Stack spacing={3}>
            {/* Billing mode — the top-level option this client is on */}
            <Box>
              <TextField
                label="Billing mode"
                value={form.billing_mode}
                onChange={(e) =>
                  setForm({ ...form, billing_mode: e.target.value })
                }
                select
                fullWidth
                helperText="Agency-assigned. The backend bills strictly by this — the threshold settings below only apply when mode = Threshold."
              >
                {BILLING_MODE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>

              {form.billing_mode === 'client_credit_line' && (
                <TextField
                  label="Net terms"
                  value={form.net_terms_days}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      net_terms_days: Number(e.target.value),
                    })
                  }
                  select
                  fullWidth
                  sx={{ mt: 2 }}
                  helperText="Fee-only invoice due date = issue date + this. Run generate_credit_line_invoices monthly to bill."
                >
                  {NET_TERMS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {form.billing_mode === 'prepay' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Prepay is funded manually. Record payments with the{' '}
                  <code>record_prepayment</code> command — spend depletes the
                  balance; campaigns pause if it runs out.
                </Alert>
              )}

              <FormControlLabel
                sx={{ mt: 2 }}
                control={
                  <Switch
                    checked={form.client_billing_ui_enabled}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        client_billing_ui_enabled: e.target.checked,
                      })
                    }
                  />
                }
                label={
                  <Box>
                    <Typography>Show new billing UI to this client</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Front-end visibility only (test cohort). Does not change
                      how they're billed.
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={form.attach_meta_charges}
                    onChange={(e) =>
                      setForm({ ...form, attach_meta_charges: e.target.checked })
                    }
                  />
                }
                label={
                  <Box>
                    <Typography>Attach Meta charges to invoices</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Client-funded clients only. Shows Meta&apos;s own ad charges on
                      their invoices and attaches them as a CSV when an invoice is
                      sent. On by default.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Divider />

            {/* Enable toggle — gated on having a card on file */}
            {(() => {
              const hasCard = (paymentMethods?.methods || []).some(
                (m) => m.type === 'card',
              );
              const canEnable = hasCard || form.threshold_billing_enabled;
              return (
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.threshold_billing_enabled}
                        disabled={!canEnable}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            threshold_billing_enabled: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Box>
                        <Typography>Threshold billing enabled</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Off: this client is not auto-charged. On: monitor processes them every 15 min.
                        </Typography>
                      </Box>
                    }
                  />
                  {!hasCard && !form.threshold_billing_enabled && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      A card must be on file before threshold billing can be
                      enabled. Add a card below and try again.
                    </Alert>
                  )}
                </Box>
              );
            })()}

            <Divider />

            {/* Threshold slider */}
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="body2">Charge threshold</Typography>
                <Typography variant="h6">
                  {formatCurrency(form.threshold_amount)}
                </Typography>
              </Stack>
              <Slider
                value={Number(form.threshold_amount) || sliderMin}
                onChange={(_, value) =>
                  setForm((prev) => ({ ...prev, threshold_amount: value }))
                }
                min={sliderMin}
                max={sliderMax}
                step={sliderStep}
                marks={
                  recommendedThreshold
                    ? [
                        { value: sliderMin, label: formatCurrency(sliderMin) },
                        {
                          value: recommendedThreshold,
                          label: `★ ${formatCurrency(recommendedThreshold)}`,
                        },
                        { value: sliderMax, label: formatCurrency(sliderMax) },
                      ]
                    : [
                        { value: sliderMin, label: formatCurrency(sliderMin) },
                        { value: sliderMax, label: formatCurrency(sliderMax) },
                      ]
                }
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => formatCurrency(v)}
              />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 3 }}
              >
                <Typography variant="caption" color="text.secondary">
                  Range allowed for {tierName || 'this tier'}: {formatCurrency(sliderMin)}–{formatCurrency(sliderMax)}
                </Typography>
                {recommendedThreshold && (
                  <Button size="small" onClick={handleApplyRecommendation}>
                    Apply recommendation ({formatCurrency(recommendedThreshold)})
                  </Button>
                )}
              </Stack>
            </Box>

            <Divider />

            {/* Payment method */}
            <TextField
              label="Payment method"
              value={form.payment_method_priority}
              onChange={(e) => {
                // ACH-primary is no longer gated behind prepaid — staff
                // can set any priority (still tier-checked server-side).
                setForm({ ...form, payment_method_priority: e.target.value });
              }}
              select
              fullWidth
              helperText={
                !tierAchEligible
                  ? `ACH is locked on ${tierName || 'this tier'}.`
                  : 'Card-only is the simplest option. ACH-primary requires prepaid (lower fees, no per-trip float). ACH-as-fallback is fine without prepaid.'
              }
            >
              {PRIORITY_OPTIONS.map((opt) => {
                const isAch = ACH_PRIORITIES.has(opt.value);
                const disabled = isAch && !tierAchEligible;
                return (
                  <MenuItem key={opt.value} value={opt.value} disabled={disabled}>
                    {opt.label}
                    {disabled ? ' (tier-locked)' : ''}
                  </MenuItem>
                );
              })}
            </TextField>

            {/* ACH savings hint */}
            {cardOnly && Number(savings) > 0 && (
              <Alert
                severity={tierAchEligible ? 'success' : 'info'}
                icon={<SavingsIcon />}
                variant="outlined"
              >
                {tierAchEligible
                  ? `Switching this client to ACH would save them ~${formatCurrency2dp(savings)}/month in Stripe fees.`
                  : `ACH would save ~${formatCurrency2dp(savings)}/mo — but ${tierName || 'this tier'} isn't ACH-eligible yet.`}
              </Alert>
            )}

            {/* Advanced — collapsed by default */}
            <Accordion variant="outlined" sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" color="text.secondary">
                  Advanced settings
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.auto_pause_on_failure}
                        onChange={(e) =>
                          setForm({ ...form, auto_pause_on_failure: e.target.checked })
                        }
                      />
                    }
                    label="Auto-pause Facebook campaigns after 2 failed charges"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.threshold_locked_by_staff}
                        onChange={(e) =>
                          setForm({ ...form, threshold_locked_by_staff: e.target.checked })
                        }
                      />
                    }
                    label={
                      <Box>
                        <Typography>Lock threshold (client cannot self-adjust)</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Disables the slider on the client portal. Use for accounts with recent payment issues or active billing negotiation.
                        </Typography>
                      </Box>
                    }
                  />
                  <Typography variant="caption" color="text.secondary">
                    Velocity cap is auto-set to {MAX_UNBILLED_MULTIPLIER}× the threshold (
                    {formatCurrency(form.threshold_amount * MAX_UNBILLED_MULTIPLIER)}). Spend exceeding this force-pauses the client regardless of charge state.
                  </Typography>
                  <TextField
                    label="Internal notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    multiline
                    rows={2}
                    fullWidth
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Save + ops actions */}
            <Stack direction="row" spacing={1} justifyContent="space-between">
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => setConfirmCharge(true)}
                  disabled={busyAction !== null}
                >
                  Charge Now
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="warning"
                  startIcon={<ReplayIcon />}
                  onClick={() => setConfirmResume(true)}
                  disabled={busyAction !== null || liveStatus?.state === 'healthy'}
                >
                  Resume
                </Button>
                {(() => {
                  // "Enable prepaid" is only meaningful when the client is
                  // ACH-eligible AND not already on prepaid. ACTIVE / FUNDING
                  // are already enrolled; FROZEN needs ops first.
                  const pb = liveStatus?.prepaid_balance;
                  const alreadyEnrolled =
                    pb &&
                    ['active', 'funding', 'draining'].includes(pb.state);
                  const blocked = pb && pb.state === 'frozen';
                  if (!liveStatus?.tier_ach_eligible) return null;
                  if (alreadyEnrolled) return null;
                  if (blocked) return null;
                  return (
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      onClick={handleEnablePrepaid}
                      disabled={busyAction !== null}
                    >
                      Enable prepaid ACH
                    </Button>
                  );
                })()}
              </Stack>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* CARD 2 — Recent charges */}
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title="Recent charges"
          subheader={
            liveStatus?.last_threshold_charge_at
              ? `Last charge: ${formatDateTime(liveStatus.last_threshold_charge_at)}`
              : 'No charges yet'
          }
        />
        <CardContent sx={{ pt: 0 }}>
          {ledgerLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Loading…
              </Typography>
            </Stack>
          ) : ledgerEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No charges yet.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Triggered</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Outcome</TableCell>
                    <TableCell>Note</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ledgerEntries.slice(0, 10).map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{formatDateTime(row.triggered_at)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency2dp(row.cumulative_spend)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.outcome_display || row.outcome}
                          color={OUTCOME_CHIP_COLORS[row.outcome] || 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {row.failure_reason || ''}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* CARD 3 — Stripe payment methods (compact) */}
      <Card>
        <CardHeader
          title="Payment methods on file"
          action={
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddCardIcon />}
                onClick={() => setAddCardOpen(true)}
              >
                Add card
              </Button>
              {tierAchEligible && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AccountBalanceIcon />}
                  onClick={() => setAddBankOpen(true)}
                >
                  Add bank (ACH)
                </Button>
              )}
              {paymentMethods?.stripe_dashboard_url && (
                <Button
                  size="small"
                  endIcon={<OpenInNewIcon />}
                  href={paymentMethods.stripe_dashboard_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Stripe Dashboard
                </Button>
              )}
            </Stack>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          {(() => {
            const priority = form.payment_method_priority || 'card';
            const order =
              priority === 'ach_then_card'
                ? ['Bank (ACH)', 'Card']
                : priority === 'card_then_ach'
                  ? ['Card', 'Bank (ACH)']
                  : ['Card', null];
            return (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1.5 }}
              >
                Routing: <strong>{order[0]}</strong>
                {order[1] ? (
                  <>, falling back to <strong>{order[1]}</strong>.</>
                ) : (
                  ' only.'
                )}
              </Typography>
            );
          })()}
          {methodsLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Loading from Stripe…
              </Typography>
            </Stack>
          ) : paymentMethods?.note ? (
            <Alert severity="warning">{paymentMethods.note}</Alert>
          ) : paymentMethods?.methods?.length ? (
            <Stack spacing={1.5}>
              {(() => {
                const health = summarizePaymentHealth(paymentMethods);
                if (!health.anyIssues) return null;
                const pendingBankWithUrl = health.pendingBanks.find(
                  (b) => b.verification_url,
                );
                const lines = [];
                if (health.pendingBanks.length) {
                  lines.push(
                    `${health.pendingBanks.length} bank${
                      health.pendingBanks.length === 1 ? ' is' : 's are'
                    } awaiting microdeposit verification.`,
                  );
                }
                if (health.expired.length) {
                  lines.push(
                    `${health.expired.length} expired card${
                      health.expired.length === 1 ? '' : 's'
                    } on file.`,
                  );
                }
                if (health.expiring.length) {
                  lines.push(
                    `${health.expiring.length} card${
                      health.expiring.length === 1 ? '' : 's'
                    } expiring within 60 days.`,
                  );
                }
                if (health.failingMethods.length) {
                  lines.push(
                    'Most recent charge attempt was declined — review the primary method.',
                  );
                }
                if (paymentMethods.methods.length > 0 && !health.hasPrimary) {
                  lines.push('No primary payment method set.');
                }
                const severity =
                  health.expired.length ||
                  health.failingMethods.length ||
                  (paymentMethods.methods.length > 0 && !health.hasPrimary)
                    ? 'error'
                    : 'warning';
                return (
                  <Alert
                    severity={severity}
                    sx={{ mb: 0.5 }}
                    action={
                      pendingBankWithUrl ? (
                        <Button
                          size="small"
                          color="inherit"
                          href={pendingBankWithUrl.verification_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Verify deposits →
                        </Button>
                      ) : null
                    }
                  >
                    {lines.length === 1 ? (
                      lines[0]
                    ) : (
                      <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                        {lines.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </Stack>
                    )}
                  </Alert>
                );
              })()}
              {paymentMethods.methods.map((m) => {
                const isUnverifiedBank = isUnverifiedBankFn(m);
                const expStatus = cardExpirationStatus(m);
                const expLabel = formatCardExpiration(m);
                const lastChargeLine = formatLastChargeLine(m);
                const lastFailed =
                  m.last_charge && m.last_charge.succeeded === false;
                return (
                  <Stack
                    key={m.id}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Stack spacing={0.25}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2">{m.label}</Typography>
                        <Chip
                          size="small"
                          label={m.type === 'card' ? 'Card' : 'ACH'}
                          color={m.type === 'card' ? 'primary' : 'success'}
                          variant="outlined"
                        />
                        {m.is_default && (
                          <Chip size="small" label="Primary" color="success" />
                        )}
                        {m.type === 'us_bank_account' && !isUnverifiedBank && (
                          <Chip size="small" label="Verified" color="success" variant="outlined" />
                        )}
                        {isUnverifiedBank && (
                          <Chip
                            size="small"
                            label="Pending verification"
                            color="warning"
                          />
                        )}
                        {expStatus === 'expired' && (
                          <Chip size="small" label="Expired" color="error" />
                        )}
                        {expStatus === 'expiring' && (
                          <Chip
                            size="small"
                            label="Expires soon"
                            color="warning"
                          />
                        )}
                        {lastFailed && (
                          <Chip
                            size="small"
                            label="Last charge declined"
                            color="error"
                            variant="outlined"
                          />
                        )}
                        {isUnverifiedBank && m.verification_url && (
                          <MuiLink
                            href={m.verification_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="caption"
                          >
                            Finish verifying →
                          </MuiLink>
                        )}
                      </Stack>
                      {(expLabel || lastChargeLine) && (
                        <Typography variant="caption" color="text.secondary">
                          {expLabel}
                          {expLabel && lastChargeLine ? ' • ' : ''}
                          {lastChargeLine}
                          {lastFailed && m.last_charge.error_message
                            ? ` — ${m.last_charge.error_message}`
                            : ''}
                        </Typography>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      {!m.is_default && !isUnverifiedBank && expStatus !== 'expired' && (
                        <Button
                          size="small"
                          onClick={async () => {
                            try {
                              await setDefaultPaymentMethod(profile.id, m.id);
                              toast.success(`Set ${m.label} as primary.`);
                              refreshPaymentMethods();
                            } catch (err) {
                              toast.error(
                                err?.response?.data?.detail ||
                                  err?.message ||
                                  'Failed to set primary.',
                              );
                            }
                          }}
                        >
                          Set primary
                        </Button>
                      )}
                      {!m.is_default && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setMethodToRemove(m)}
                        >
                          Remove
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No payment methods on file. Click <strong>Add card</strong> to attach one.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmCharge} onClose={() => setConfirmCharge(false)}>
        <DialogTitle>Charge now?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Creates a threshold invoice for{' '}
            <strong>{formatCurrency2dp(liveStatus?.unbilled_spend)}</strong> of unbilled spend and immediately charges the client's primary payment method.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCharge(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleChargeNow} disabled={busyAction !== null}>
            Charge now
          </Button>
        </DialogActions>
      </Dialog>

      <AddCardDialog
        open={addCardOpen}
        onClose={() => setAddCardOpen(false)}
        profileId={profile?.id}
        isFirstCard={!paymentMethods?.methods?.length}
        onAttached={refreshPaymentMethods}
      />

      <AddBankDialog
        open={addBankOpen}
        onClose={() => setAddBankOpen(false)}
        profileId={profile?.id}
        createSetupIntent={createSetupIntent}
        registerPendingVerification={registerPendingBankVerification}
        onAttached={async (newPmId) => {
          // Optionally promote to primary if this is the first method on file.
          // Skip when verification is pending — we don't want to auto-route
          // charges to a bank that can't be charged yet.
          if (newPmId && !paymentMethods?.methods?.length) {
            try {
              await setDefaultPaymentMethod(profile.id, newPmId);
            } catch {
              /* non-fatal */
            }
          }
          refreshPaymentMethods();
        }}
        settlementCopy={
          'ACH transfers settle in 3–5 business days. Staff: expect the first ' +
          'few charges to overlap until cadence stabilizes.'
        }
      />

      <RemovePaymentMethodDialog
        open={Boolean(methodToRemove)}
        method={methodToRemove}
        onClose={() => setMethodToRemove(null)}
        onConfirm={async (m) => {
          await detachPaymentMethod(profile.id, m.id);
          toast.success('Payment method removed.');
          setMethodToRemove(null);
          refreshPaymentMethods();
        }}
      />

      {/* EnablePrepaidRequiredDialog removed — the ACH-primary gate it
          backed is gone. The Confirm dialog below stays: it's the
          staff-driven reactive enrollment tool (the by-exception whale
          prepaid path), triggered by an explicit staff action. */}
      <EnablePrepaidConfirmDialog
        open={prepaidConfirmOpen}
        fetchTerms={() => fetchPrepaidTerms(profile.id)}
        onClose={() => setPrepaidConfirmOpen(false)}
        onSubmit={async ({ termsVersion }) => {
          const resp = await enablePrepaid(profile.id, { termsVersion });
          const pi = resp?.data?.stripe_payment_intent_id || '';
          toast.success(
            `Initial deposit fired${pi ? ` (PI ${pi})` : ''}. State will ` +
              'flip to ACTIVE on settled-succeeded webhook.',
          );
          setPrepaidConfirmOpen(false);
          await loadAll();
        }}
      />

      <Dialog open={confirmResume} onClose={() => setConfirmResume(false)}>
        <DialogTitle>Resume billing?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Resets the failure counter, flips state to healthy, and unpauses Facebook campaigns that the billing engine paused. Use only after the underlying payment issue is resolved.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmResume(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleResume}
            disabled={busyAction !== null}
          >
            Resume
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
